"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Avatar, Button, Carousel, Image as AntImage, Space } from "antd";
import { PauseCircleOutlined, PlayCircleOutlined } from "@ant-design/icons";
import Title from "antd/lib/typography/Title";
import Paragraph from "antd/lib/typography/Paragraph";
import Text from "antd/lib/typography/Text";
import { getLargeSrc } from "@/lib/helpers";

import type { PhotoEngagement } from "@/lib/api/photoEngagement";
import { usePhotoEngagement } from "@/lib/hooks/usePhotoEngagement";

type WorkPhoto = PhotoEngagement & {
  _id: string;
  src: string;
  alt?: string;
  type?: "image" | "video";
  variants?: string[];
  user?: {
    name?: string;
    photo?: string;
  };
};

const sliderDefaults = {
  dots: false,
  infinite: true,
  speed: 600,
  slidesToScroll: 1,
  arrows: true,
  // antd v6 Carousel defaults `draggable` to false, which disables mouse-drag
  // navigation (touch still works). Restore react-slick's old default so the
  // carousel can be dragged on desktop and touch devices.
  draggable: true,
  autoplay: true,
  autoplaySpeed: 2000,
  pauseOnHover: true,
  pauseOnFocus: true,
  cssEase: "linear",
};

// Widest layout used for SSR / first client render so hydration matches.
const DEFAULT_SLIDES_TO_SHOW = 6;

// react-slick's `responsive` only reacts to matchMedia *change* events, so a
// page loaded already-narrow never applies the breakpoint and stays at the
// base count. Compute `slidesToShow` ourselves instead — correct on mount and
// on resize.
const getSlidesToShow = (width: number) => {
  if (width <= 992) return 2;
  if (width <= 1200) return 4;
  return DEFAULT_SLIDES_TO_SHOW;
};

type WorkPhotoGalleryProps = {
  initialPhotos?: WorkPhoto[];
};

const WorkPhotoGallery = ({ initialPhotos = [] }: WorkPhotoGalleryProps) => {
  const { root } = usePhotoEngagement();
  const photos = useMemo(
    () => initialPhotos.filter((item) => item.type !== "video"),
    [initialPhotos],
  );

  const [slidesToShow, setSlidesToShow] = useState(DEFAULT_SLIDES_TO_SHOW);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [userAutoplayChoice, setUserAutoplayChoice] = useState<boolean | null>(
    null,
  );

  useEffect(() => {
    const updateSlidesToShow = () => {
      const next = getSlidesToShow(window.innerWidth);
      setSlidesToShow((prev) => (prev === next ? prev : next));
    };

    updateSlidesToShow();
    window.addEventListener("resize", updateSlidesToShow);
    return () => window.removeEventListener("resize", updateSlidesToShow);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);

    updatePreference();
    mediaQuery.addEventListener("change", updatePreference);
    return () => mediaQuery.removeEventListener("change", updatePreference);
  }, []);

  const shouldAutoplay =
    userAutoplayChoice ?? (photos.length > 1 && !prefersReducedMotion);

  const sliderSettings = useMemo(
    () => ({
      ...sliderDefaults,
      slidesToShow,
      autoplay: shouldAutoplay,
    }),
    [shouldAutoplay, slidesToShow],
  );

  const toggleAutoplay = () => {
    setUserAutoplayChoice((current) => {
      const isCurrentlyPlaying =
        current ?? (photos.length > 1 && !prefersReducedMotion);
      return !isCurrentlyPlaying;
    });
  };

  return (
    <section
      className="work-gallery-section section"
      aria-labelledby="work-gallery"
    >
      <div className="work-gallery-header">
        <Title level={3} id="work-gallery">
          Work Photo
        </Title>
        <Paragraph>
          Our latest work, created daily by our team across all locations.
          <br className="is-hidden-touch" />{" "}
          Browse styles, get inspired, and find your next cut.
        </Paragraph>
        {photos.length > 1 ? (
          <Button
            type="text"
            icon={
              shouldAutoplay ? (
                <PauseCircleOutlined />
              ) : (
                <PlayCircleOutlined />
              )
            }
            onClick={toggleAutoplay}
          >
            {shouldAutoplay ? "Pause slideshow" : "Play slideshow"}
          </Button>
        ) : null}
      </div>
      <div className="work-gallery-slider" ref={root}>
        <Carousel {...sliderSettings}>
          {!photos.length
            ? Array.from({ length: 4 }).map((_, index) => (
                <div
                  className="work-gallery-slide"
                  key={`placeholder-${index}`}
                >
                  <div className="work-gallery-placeholder__item" />
                </div>
              ))
            : photos.map((photo) => {
                const src = photo.src ? getLargeSrc(photo.src) : "/logo.png";

                return (
                  <div className="work-gallery-slide" key={photo._id}>
                    <AntImage
                      data-work-photo-id={photo.workPhotoId}
                      data-salon-id={photo.salonId}
                      src={src}
                      alt={photo.alt || "Work photo"}
                      preview={false}
                      className="work-gallery-image"
                      loading="lazy"
                    />
                    {photo.user?.name ? (
                      <div className="work-gallery-caption">
                        <Space size={8} align="center">
                          <Avatar
                            src={
                              photo.user.photo ? (
                                // User-managed avatars are not constrained to
                                // the Next.js image-host allowlist.
                                // eslint-disable-next-line @next/next/no-img-element
                                <img
                                  src={photo.user.photo}
                                  alt=""
                                  width={24}
                                  height={24}
                                  loading="lazy"
                                />
                              ) : undefined
                            }
                            alt={photo.user.name}
                            size={24}
                          >
                            {photo.user.name?.charAt(0)}
                          </Avatar>
                          <Text style={{ color: "rgba(255, 255, 255, 0.95)" }}>
                            {photo.user.name}
                          </Text>
                        </Space>
                      </div>
                    ) : null}
                  </div>
                );
              })}
        </Carousel>
      </div>
      <div className="work-gallery-cta">
        <Button href="/photos" color="default" variant="filled">
          Show more photos
        </Button>
      </div>
    </section>
  );
};

export default WorkPhotoGallery;
